import React, { useState, useMemo } from "react";
import { Briefcase, Plus, Users, Trash2, Search, ArrowRight } from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import type { WorkspaceItem, WorkspaceDetail, MemberRow, OrgStats } from "../types";
import { getErrorMessage } from "../types";
import { CreateWorkspaceModal, ConfirmActionModal, type ConfirmAction } from "../components/modals";
import { WorkspaceDetailPage } from "./WorkspaceDetailPage";

export interface WorkspacesPageProps {
  slug: string;
  stats: OrgStats;
  workspaces: WorkspaceItem[];
  activeWorkspaceSlug?: string | null;
  onSelectWorkspaceForDetail?: (wsSlug: string | null) => void;
  detailedWorkspace: WorkspaceDetail | null;
  loadingWorkspaceDetail: boolean;
  members: MemberRow[];
  loadWorkspaces: (force?: boolean) => Promise<void>;
  loadWorkspaceDetail: (wsSlug: string, force?: boolean) => Promise<void>;
  loadMembers: (force?: boolean) => Promise<void>;
  selectedWorkspace?: WorkspaceItem | null;
  setSelectedWorkspace?: (ws: WorkspaceItem | null) => void;
}

export const WorkspacesPage: React.FC<WorkspacesPageProps> = ({
  slug,
  stats,
  workspaces,
  activeWorkspaceSlug,
  onSelectWorkspaceForDetail,
  detailedWorkspace,
  loadingWorkspaceDetail,
  members,
  loadWorkspaces,
  loadWorkspaceDetail,
  loadMembers,
  setSelectedWorkspace,
}) => {
  const [showCreateWsModal, setShowCreateWsModal] = useState(false);
  const [localViewingWsSlug, setLocalViewingWsSlug] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const currentViewingSlug =
    activeWorkspaceSlug !== undefined ? activeWorkspaceSlug : localViewingWsSlug;

  const handleSetViewingSlug = (wsSlug: string | null) => {
    if (onSelectWorkspaceForDetail) {
      onSelectWorkspaceForDetail(wsSlug);
    } else {
      setLocalViewingWsSlug(wsSlug);
    }
  };

  const handleDeleteWorkspace = (ws: WorkspaceItem) => {
    if (ws.is_default) {
      alert("The default workspace cannot be deleted.");
      return;
    }
    setConfirmAction({
      title: "Delete workspace?",
      description: `Delete ${ws.name}? Members will remain in the organization, but workspace-specific access and assignments will be removed.`,
      confirmLabel: "Delete workspace",
      onConfirm: async () => {
        try {
          const { error } = await api.deleteWorkspaceApiV1B2bSlugWorkspacesWsSlugDelete({
            path: { slug, ws_slug: ws.slug },
          });
          if (!error) {
            if (currentViewingSlug === ws.slug) {
              handleSetViewingSlug(null);
            }
            await loadWorkspaces(true);
          } else {
            alert(`Failed to delete workspace: ${getErrorMessage(error, "Unknown error")}`);
          }
        } catch (err) {
          console.error("Error deleting workspace:", err);
        }
      },
    });
  };

  // Filtered workspaces based on search query
  const filteredWorkspaces = useMemo(() => {
    if (!searchQuery.trim()) return workspaces;
    const q = searchQuery.toLowerCase().trim();
    return workspaces.filter(
      (ws) =>
        ws.name.toLowerCase().includes(q) ||
        ws.slug.toLowerCase().includes(q) ||
        (ws.description && ws.description.toLowerCase().includes(q))
    );
  }, [workspaces, searchQuery]);

  // If a specific workspace is being viewed in detail, render WorkspaceDetailPage
  const viewingWorkspace = currentViewingSlug
    ? workspaces.find((w) => w.slug === currentViewingSlug)
    : null;

  if (viewingWorkspace) {
    return (
      <WorkspaceDetailPage
        slug={slug}
        workspace={viewingWorkspace}
        detailedWorkspace={detailedWorkspace}
        loadingWorkspaceDetail={loadingWorkspaceDetail}
        members={members}
        stats={stats}
        onBack={() => handleSetViewingSlug(null)}
        loadWorkspaceDetail={loadWorkspaceDetail}
        loadWorkspaces={loadWorkspaces}
        loadMembers={loadMembers}
      />
    );
  }

  return (
    <div className="space-y-8">
      <ConfirmActionModal action={confirmAction} onClose={() => setConfirmAction(null)} />
      {/* Header with Create Workspace Action */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl shadow-2xs">
              <Briefcase className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Organization Workspaces</h2>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1.5 max-w-2xl leading-relaxed">
            Divide {stats.organization_name} into focused workspaces (e.g. Engineering, Sales, Leadership). Each workspace can have scoped documents, custom courses, and tailored learning benchmarks.
          </p>
        </div>

        <button
          onClick={() => setShowCreateWsModal(true)}
          className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-xs flex items-center gap-2 transition shrink-0 active:scale-95 cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          Create Workspace
        </button>
      </div>

      {/* Search & Workspaces Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            All Workspaces ({workspaces.length})
          </h3>
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Search workspaces..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 shadow-2xs"
          />
        </div>
      </div>

      {/* Workspaces Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredWorkspaces.map((ws) => (
          <div
            key={ws.id}
            onClick={() => {
              handleSetViewingSlug(ws.slug);
              if (setSelectedWorkspace) setSelectedWorkspace(ws);
            }}
            className="cursor-pointer bg-white rounded-3xl border border-slate-200 p-6 transition flex flex-col justify-between shadow-xs hover:border-indigo-300 hover:shadow-md group relative"
          >
            <div>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-bold text-slate-900 text-base group-hover:text-indigo-600 transition truncate">
                    {ws.name}
                  </span>
                  {ws.is_default && (
                    <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold uppercase rounded-md flex-shrink-0">
                      Default
                    </span>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 line-clamp-2 min-h-[32px] leading-relaxed">
                {ws.description || "General team collaboration and learning workspace."}
              </p>
            </div>

            <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 text-slate-600 font-medium">
                <Users className="w-3.5 h-3.5 text-slate-400" />
                <span>
                  {ws.member_count} {ws.member_count === 1 ? "member" : "members"}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[11px] font-mono text-slate-400 group-hover:text-indigo-600 transition flex items-center gap-1">
                  /{ws.slug}
                  <ArrowRight className="w-3 h-3 text-indigo-500 opacity-0 group-hover:opacity-100 transition" />
                </span>
                {!ws.is_default && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDeleteWorkspace(ws);
                    }}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition cursor-pointer"
                    title="Delete workspace"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Create New Workspace Card CTA */}
        <button
          onClick={() => setShowCreateWsModal(true)}
          className="border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/20 rounded-3xl p-6 transition flex flex-col items-center justify-center text-center group min-h-[160px] cursor-pointer"
        >
          <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-2 group-hover:scale-110 transition shadow-2xs">
            <Plus className="w-5 h-5" />
          </div>
          <span className="font-bold text-slate-800 text-sm group-hover:text-indigo-600 transition">
            Create New Workspace
          </span>
          <span className="text-xs text-slate-400 mt-1">Add a team or division workspace</span>
        </button>
      </div>

      {filteredWorkspaces.length === 0 && searchQuery && (
        <div className="text-center py-12 bg-white rounded-3xl border border-slate-200 p-8 shadow-xs">
          <Briefcase className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <h4 className="text-sm font-bold text-slate-800">No workspaces match &quot;{searchQuery}&quot;</h4>
          <p className="text-xs text-slate-400 mt-1">Try a different search query or clear the filter.</p>
        </div>
      )}

      {/* Create Workspace Modal */}
      <CreateWorkspaceModal
        isOpen={showCreateWsModal}
        onClose={() => setShowCreateWsModal(false)}
        slug={slug}
        onSuccess={async (newWs) => {
          await loadWorkspaces(true);
          handleSetViewingSlug(newWs.slug);
        }}
      />
    </div>
  );
};
